import { forwardRef, useImperativeHandle, useRef } from "react";
import { MGTariff } from "./MGTariff";
import { WEEK_DAYS } from "./TarifUtils";
import { useDualTranslation } from "../utils/i18nHelpers";
import {
  useTariffSnackbar,
  TariffSnackbar,
  sortTimeSlots,
  validateTimeSlotsCoverage,
  validateTimeSlotsContiguous,
  validateWeekdaysCoverage,
} from "../utils/tariffValidationUtils";

const Tariff = forwardRef((props, ref) => {
  const { t } = useDualTranslation();
  const mgTariffRef = useRef();
  const { snackbarId, snackbarProps, callSnackbar } = useTariffSnackbar(
    t("tariff.snackbar.success")
  );
  const handleValidate = () => {
    const jsonData = mgTariffRef.current.getJsonData();
    if (validateJsonData(jsonData)) {
      return { error: false, message: "Validation passed", data: jsonData };
    } else {
      return { error: true, message: "Validation failed" };
    }
  };

  const validateJsonData = (jsonData) => {
    if (!jsonData || jsonData.length === 0) return false;

    for (let i = 0; i < jsonData.length; i++) {
      const season = jsonData[i];

      for (let j = 0; j < season.periods.length; j++) {
        const dayGroup = season.periods[j];
        const groupName = dayGroup?.dayGroupName || "Group";
        const timeSlots = dayGroup.rate.flatMap((tariff) => tariff.timeSlots);

        // Check if time slots exist
        if (timeSlots.length === 0) {
          callSnackbar(
            "error",
            t("tariff.validation.addTimeSlots", { group: groupName })
          );
          return false;
        }

        // Validate 24h coverage
        const coverageResult = validateTimeSlotsCoverage(timeSlots);
        if (!coverageResult.valid) {
          callSnackbar(
            "error",
            t("tariff.validation.incomplete24h", { group: groupName })
          );
          return false;
        }

        // Validate contiguous time slots
        const contiguousResult = validateTimeSlotsContiguous(timeSlots);
        if (!contiguousResult.valid) {
          callSnackbar(
            "error",
            t("tariff.validation.addTimeSlots", { group: groupName })
          );
          return false;
        }
      }

      // Validate weekdays coverage
      if (!validateWeekdaysCoverage(season.periods)) {
        callSnackbar(
          "error",
          t("tariff.validation.allWeekdays")
        );
        return false;
      }
    }

    return true;
  };

  useImperativeHandle(ref, () => ({
    handleValidate,
    getJsonData: () => mgTariffRef.current?.getJsonData?.(),
  }));

  const { type, message } = snackbarProps;
  return (
    <>
      <TariffSnackbar id={snackbarId} type={type} message={message} />
      <div direction="column">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <MGTariff
                ref={mgTariffRef}
                allowBill={props.allowBill || false}
                allowPenalty={props.allowPenalty || false}
                tariffTemplate={
                  props.tariffTemplate?.length ? props.tariffTemplate : null
                }
                isVersionCompleted={props.isVersionCompleted || false}
                getFieldAttributes={props?.getFieldAttributes}
                onChange={props?.onChange}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default Tariff;
